import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { Check, ArrowRight, ListChecks } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbLink, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { ProductGallery } from "@/components/ProductGallery";
import { ProductCard } from "@/components/ProductCard";
import { TrustBand } from "@/components/TrustBand";
import { SectionHeading } from "@/components/SectionHeading";
import { Reveal, RevealGroup, RevealItem } from "@/components/motion/Reveal";
import { AnimatedPrice } from "@/components/motion/AnimatedPrice";
import { getProduct, getProductReviews } from "@/lib/api";
import { metaViewContent, metaAddToCart } from "@/lib/meta";
import { formatPrice } from "@/lib/format";
import { CHECKLIST_GROUPS, GRADE_INFO } from "@/data/content";
import { useCart } from "@/context/CartContext";
import { ProductRating, ProductReviewsSection, ReviewsJsonLd } from "@/components/ProductReviews";
import { cn } from "@/lib/utils";
import {
  GRADE_LABELS,
  GRADE_DESC,
  colorList,
  memoryList,
  gradeList,
  hasColors as productHasColors,
  hasMemory as productHasMemory,
  colorEnabled,
  memoryEnabled,
  gradeEnabled,
  findVariant,
  normalizeSelection,
  defaultSelection,
  effectivePrice,
  galleryPhotos,
  variantLabel,
  cloudinaryThumb,
} from "@/lib/variants";

const TOTAL_CHECKS = CHECKLIST_GROUPS.reduce((s, g) => s + g.tests, 0);

// Pulsante pill per Memoria / Condizione. Selezionato = bordo+riempimento chalk
// (mai lime: il lime è riservato alla CTA). Non disponibile = opaco e non cliccabile.
function Pill({ isSel, enabled, onClick, testid, children }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={isSel}
      aria-disabled={!enabled}
      disabled={!enabled}
      onClick={onClick}
      data-testid={testid}
      data-selected={isSel ? "true" : "false"}
      className={cn(
        "rounded-full border px-4 py-2 text-sm font-medium transition-[border-color,background-color,color] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        isSel
          ? "border-foreground bg-foreground text-ink"
          : "border-border bg-transparent text-foreground hover:border-foreground/50",
        !enabled && "cursor-not-allowed border-border/40 bg-transparent text-muted-foreground/40 line-through hover:border-border/40"
      )}
    >
      {children}
    </button>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const { addItem } = useCart();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [sel, setSel] = useState({ color_id: null, memory: null, grade: null });
  const ctaRef = useRef(null);
  const [showBar, setShowBar] = useState(false);
  const [reviewsData, setReviewsData] = useState(null);

  useEffect(() => {
    setData(null);
    setError(false);
    setReviewsData(null);
    getProduct(slug)
      .then((res) => {
        setData(res);
        setSel(defaultSelection(res.product));
        try {
          const p = res.product;
          const def = defaultSelection(p);
          const dv = (p.variants || []).find((v) => v.grade === def.grade) || (p.variants || [])[0] || {};
          metaViewContent({
            contentId: dv.id || p.id,
            name: p.name,
            value: effectivePrice(dv),
          });
        } catch (e) { /* noop */ }
        getProductReviews(res.product.id)
          .then(setReviewsData)
          .catch(() => setReviewsData({ summary: { count: 0 }, reviews: [] }));
      })
      .catch(() => setError(true));
  }, [slug]);

  // Barra acquisto mobile: appare quando la CTA principale esce dal viewport.
  useEffect(() => {
    const check = () => {
      const el = ctaRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setShowBar(rect.bottom < 72 || rect.top > window.innerHeight);
    };
    check();
    window.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check);
    return () => {
      window.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
    };
  }, [data]);

  const product = data?.product;
  const colors = useMemo(() => (product ? colorList(product) : []), [product]);
  const memories = useMemo(() => (product ? memoryList(product) : []), [product]);
  const grades = useMemo(() => (product ? gradeList(product) : []), [product]);
  const wc = product ? productHasColors(product) : false;
  const wm = product ? productHasMemory(product) : false;

  const current = product ? findVariant(product, sel) : null;
  const price = effectivePrice(current);
  const newPrice = current?.compare_at_price ? Number(current.compare_at_price) : null;
  const showNew = newPrice != null && price != null && newPrice > price;
  const saving = showNew ? newPrice - price : 0;
  const qty = current?.quantity ?? 0;
  const inStock = qty > 0;

  const photos = useMemo(
    () => (product ? galleryPhotos(product, wc ? sel.color_id : null) : []),
    [product, wc, sel.color_id]
  );

  if (error) {
    return (
      <div className="container-page py-32" data-testid="error-state">
        <div className="text-sm font-medium text-carrot">Errore 404</div>
        <h1 className="display-lg mt-4">Prodotto non trovato.</h1>
        <p className="mt-3 text-muted-foreground">Il prodotto che cerchi non esiste o non è più disponibile.</p>
        <Button asChild variant="outline" className="mt-8"><Link to="/catalogo">Torna al catalogo</Link></Button>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container-page py-10" data-testid="loading-skeleton">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-6"><div className="aspect-square w-full animate-pulse rounded-2xl bg-muted" /></div>
          <div className="space-y-5 lg:col-span-6">
            <div className="h-3 w-1/4 animate-pulse rounded bg-muted" />
            <div className="h-12 w-2/3 animate-pulse rounded bg-muted" />
            <div className="h-24 w-full animate-pulse rounded bg-muted" />
            <div className="h-40 w-full animate-pulse rounded bg-muted" />
          </div>
        </div>
      </div>
    );
  }

  const related = data.related || [];
  const isNew = product.condition === "nuovo";
  const selColorName = wc ? (colors.find((c) => c.id === sel.color_id)?.name || "") : "";

  const selectColor = (cid) => setSel(normalizeSelection(product, { ...sel, color_id: cid }));
  const selectMemory = (m) => setSel(normalizeSelection(product, { ...sel, memory: m }));
  const selectGrade = (g) => setSel(normalizeSelection(product, { ...sel, grade: g }));

  const handleAdd = () => {
    if (!current || !inStock) return;
    addItem({
      product_id: product.id,
      slug: product.slug,
      name: product.name,
      brand: product.brand,
      grade: current.grade,
      color_id: current.color_id || null,
      memory: current.memory || null,
      variant_id: current.id,
      variant_label: variantLabel(product, current),
      image: photos[0]?.url || null,
      unit_price: price,
    });
    toast.success("Aggiunto al carrello", {
      description: `${product.name} · ${variantLabel(product, current)}`,
    });
    metaAddToCart({
      contentId: current.id || product.id,
      name: product.name,
      value: price,
      quantity: 1,
    });
  };

  return (
    <div className="container-page pb-24 pt-8 sm:pt-10" data-testid="product-detail-page">
      <Breadcrumb>
        <BreadcrumbList className="text-[13px] text-muted-foreground">
          <BreadcrumbItem><BreadcrumbLink asChild><Link to="/" className="hover:text-foreground">Home</Link></BreadcrumbLink></BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem><BreadcrumbLink asChild><Link to="/catalogo" className="hover:text-foreground">Catalogo</Link></BreadcrumbLink></BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem><BreadcrumbPage className="text-foreground">{product.name}</BreadcrumbPage></BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="mt-8 grid grid-cols-1 gap-10 md:grid-cols-2 md:gap-8 lg:grid-cols-12 lg:gap-14">
        {/* Galleria sticky */}
        <div className="md:col-span-1 lg:col-span-6">
          <div className="lg:sticky lg:top-[100px]">
            <Reveal>
              <ProductGallery photos={photos} name={product.name} />
            </Reveal>
          </div>
        </div>

        {/* Info */}
        <div className="md:col-span-1 lg:col-span-6">
          <Reveal>
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm font-medium text-muted-foreground">{product.brand}</span>
              <span className="rounded-full bg-surface px-3 py-1 text-xs font-medium text-foreground">
                {isNew ? "Nuovo" : "Ricondizionato"}
              </span>
            </div>
            <h1 className="display-lg mt-4 font-display text-foreground" data-testid="pdp-title">{product.name}</h1>
            <ProductRating productId={product.id} data={reviewsData} />
            <p className="mt-4 max-w-lg text-base leading-relaxed text-muted-foreground">{product.short_description}</p>
          </Reveal>

          {/* Prezzo unico */}
          <Reveal delay={0.05} className="mt-8 border-t border-border pt-6">
            <AnimatedPrice
              value={price}
              className="font-display text-4xl font-semibold tracking-tight text-foreground sm:text-5xl"
              data-testid="product-dynamic-price"
            />
            {showNew && (
              <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                <span className="text-muted-foreground">
                  Nuovo <span className="line-through" data-testid="product-new-price">{formatPrice(newPrice)}</span>
                </span>
                <span className="font-semibold text-carrot" data-testid="product-savings">
                  Risparmi {formatPrice(saving)}
                </span>
              </div>
            )}
          </Reveal>

          {/* Selettori */}
          <div className="mt-8 flex flex-col gap-7">
            {/* Colore */}
            {wc && (
              <Reveal delay={0.08}>
                <div className="text-sm font-medium text-foreground">
                  Colore: <span className="text-muted-foreground">{selColorName}</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-3" role="radiogroup" aria-label="Colore" data-testid="pdp-color-selector">
                  {colors.map((c) => {
                    const enabled = colorEnabled(product, c.id);
                    const isSel = sel.color_id === c.id;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        role="radio"
                        aria-checked={isSel}
                        aria-label={c.name}
                        title={c.name}
                        disabled={!enabled}
                        onClick={() => selectColor(c.id)}
                        data-testid={`pdp-color-${c.id}`}
                        data-selected={isSel ? "true" : "false"}
                        className={cn(
                          "relative h-10 w-10 rounded-full border-2 p-0 transition-[border-color,transform] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                          isSel ? "scale-105 border-foreground" : "border-border hover:border-foreground/50",
                          !enabled && "cursor-not-allowed opacity-30"
                        )}
                      >
                        <span className="absolute inset-[3px] rounded-full" style={{ backgroundColor: c.hex }} />
                      </button>
                    );
                  })}
                </div>
              </Reveal>
            )}

            {/* Memoria */}
            {wm && (
              <Reveal delay={0.1}>
                <div className="text-sm font-medium text-foreground">
                  Memoria: <span className="text-muted-foreground">{sel.memory ? `${sel.memory} GB` : "—"}</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-3" role="radiogroup" aria-label="Memoria" data-testid="pdp-memory-selector">
                  {memories.map((m) => (
                    <Pill
                      key={m}
                      isSel={sel.memory === m}
                      enabled={memoryEnabled(product, sel, m)}
                      onClick={() => selectMemory(m)}
                      testid={`pdp-memory-${m}`}
                    >
                      {m} GB
                    </Pill>
                  ))}
                </div>
              </Reveal>
            )}

            {/* Condizione */}
            {grades.length > 0 && (
              <Reveal delay={0.12}>
                <div className="text-sm font-medium text-foreground">
                  Condizione: <span className="text-muted-foreground">{GRADE_LABELS[sel.grade] || ""}</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-3" role="radiogroup" aria-label="Condizione" data-testid="pdp-grade-selector">
                  {grades.map((g) => (
                    <Pill
                      key={g}
                      isSel={sel.grade === g}
                      enabled={gradeEnabled(product, sel, g)}
                      onClick={() => selectGrade(g)}
                      testid={`pdp-grade-option-${g}`}
                    >
                      {GRADE_LABELS[g]}
                    </Pill>
                  ))}
                </div>
                {sel.grade && (
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground" data-testid="pdp-grade-description">
                    {GRADE_DESC[sel.grade]}
                  </p>
                )}
              </Reveal>
            )}
          </div>

          {/* Disponibilità + CTA */}
          <Reveal delay={0.15} className="mt-8">
            <div className="mb-3 text-sm" data-testid="pdp-availability">
              {inStock ? (
                qty <= 3 ? (
                  <span className="font-medium text-carrot">Ultimi {qty} pezzi disponibili</span>
                ) : (
                  <span className="text-muted-foreground">Disponibile · spedizione in 2-4 giorni</span>
                )
              ) : (
                <span className="text-muted-foreground">Momentaneamente non disponibile</span>
              )}
            </div>
            <Button
              ref={ctaRef}
              variant="lime"
              size="xl"
              className="w-full justify-between px-6"
              onClick={handleAdd}
              disabled={!current || !inStock}
              data-testid="pdp-add-to-cart-button"
            >
              <span>Aggiungi al carrello</span>
              <span className="flex items-center gap-3">
                <AnimatedPrice value={price} className="text-base" />
                <ArrowRight strokeWidth={2} />
              </span>
            </Button>
            <TrustBand className="mt-5" data-testid="pdp-trust-band" />
          </Reveal>

          {/* Cosa abbiamo verificato */}
          <Reveal delay={0.05} className="mt-10 rounded-2xl border border-border bg-card" data-testid="pdp-verified-block">
            <div className="flex items-center justify-between gap-4 border-b border-border px-5 py-4">
              <div className="flex items-center gap-3">
                <ListChecks className="h-4 w-4 text-lime" strokeWidth={1.5} />
                <h2 className="font-display text-lg font-semibold tracking-tight">Cosa abbiamo verificato</h2>
              </div>
              <span className="text-sm text-muted-foreground">{TOTAL_CHECKS} test</span>
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-2">
              {CHECKLIST_GROUPS.map((g, i) => (
                <li
                  key={g.label}
                  className={cn(
                    "flex items-center justify-between gap-3 border-border px-5 py-3.5 text-sm",
                    i < CHECKLIST_GROUPS.length - 1 ? "border-b" : "",
                    i % 2 === 0 ? "sm:border-r" : "",
                    i >= CHECKLIST_GROUPS.length - 2 ? "sm:border-b-0" : ""
                  )}
                >
                  <span className="flex items-center gap-3 text-foreground/90">
                    <Check className="h-3.5 w-3.5 shrink-0 text-lime" strokeWidth={2.5} />
                    {g.label}
                  </span>
                  <span className="text-sm text-muted-foreground">{g.tests} test</span>
                </li>
              ))}
            </ul>
            <div className="border-t border-border px-5 py-3 text-sm text-muted-foreground">
              Esito: superato · Funzionalità 100%
            </div>
          </Reveal>

          {/* Scheda tecnica */}
          <Reveal delay={0.05} className="mt-10">
            <div className="text-sm font-medium text-foreground">Scheda tecnica</div>
            <dl className="mt-4 divide-y divide-border border-y border-border" data-testid="pdp-specs">
              {(product.specs || []).map((s) => (
                <div key={s.label} className="grid grid-cols-[40%_1fr] gap-4 py-3 text-sm">
                  <dt className="text-muted-foreground">{s.label}</dt>
                  <dd className="text-foreground">{s.value}</dd>
                </div>
              ))}
            </dl>
          </Reveal>

          <Reveal delay={0.05} className="mt-8">
            <Accordion type="single" collapsible className="border-t border-border">
              <AccordionItem value="condizioni" className="border-border">
                <AccordionTrigger className="py-5 text-sm font-medium hover:no-underline">Condizioni</AccordionTrigger>
                <AccordionContent className="space-y-3 text-sm text-muted-foreground">
                  {["like_new", "used"].map((g) => (
                    <p key={g} className="flex flex-col gap-0.5">
                      <span className="font-medium text-foreground">{GRADE_LABELS[g]}</span>
                      <span>{(GRADE_INFO[g] || {}).description || GRADE_DESC[g]}</span>
                    </p>
                  ))}
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="spedizioni" className="border-border">
                <AccordionTrigger className="py-5 text-sm font-medium hover:no-underline">Spedizioni e resi</AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                  Spedizione tracciata su ogni ordine, consegna in 2-4 giorni lavorativi. Reso gratuito entro 14 giorni dalla consegna.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="descrizione" className="border-border">
                <AccordionTrigger className="py-5 text-sm font-medium hover:no-underline">Descrizione completa</AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-muted-foreground">{product.description}</AccordionContent>
              </AccordionItem>
            </Accordion>
          </Reveal>
        </div>
      </div>

      {/* Recensioni */}
      {reviewsData && <ProductReviewsSection productId={product.id} data={reviewsData} />}
      {reviewsData && <ReviewsJsonLd productName={product.name} data={reviewsData} />}

      {/* Correlati */}
      {related.length > 0 && (
        <section className="mt-24 border-t border-border pt-16 lg:mt-32" data-testid="pdp-related">
          <SectionHeading
            eyebrow="Dalla stessa categoria"
            title="Potresti essere interessato anche a…"
            size="lg"
            link={{ to: "/catalogo", label: "Tutto il catalogo", testId: "pdp-related-link" }}
          />
          <RevealGroup className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {related.map((p) => (
              <RevealItem key={p.id} className="h-full">
                <ProductCard product={p} />
              </RevealItem>
            ))}
          </RevealGroup>
        </section>
      )}

      {/* Barra acquisto sticky mobile */}
      <AnimatePresence>
        {showBar && current && (
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-x-0 bottom-0 z-40 border-t border-line bg-ink/95 backdrop-blur-xl lg:hidden"
            data-testid="pdp-mobile-buy-bar"
          >
            <div className="container-page flex items-center gap-3 py-3">
              <div className="h-11 w-11 shrink-0 overflow-hidden rounded-xl bg-transparent">
                {photos[0] ? (
                  <img src={cloudinaryThumb(photos[0].url, "e_trim/c_pad,w_160,h_160,b_transparent,f_auto,q_auto:best")} alt="" className="h-full w-full object-contain" />
                ) : null}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-foreground">{product.name}</div>
                <AnimatedPrice value={price} className="text-sm text-foreground" />
              </div>
              <Button variant="lime" onClick={handleAdd} disabled={!inStock} data-testid="pdp-mobile-add-button">
                Aggiungi
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
